// IMPORTANT:
// Only import this file once, and only in `src/index.tsx` because we do not
// want to instantiate multiple query clients within our application.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type FC, type PropsWithChildren } from "react";

import { isEnterpriseMfaStepParkedError } from "./enterpriseMfaStep";
import { isFetchNetworkError } from "./fetchNetworkError";

// Errors that fire when a stale mutation lands after the user was
// redirected to /login (no auth token in localStorage). Logging them
// via console.error sends them to RUM as a recurring, unactionable
// noise cluster (Datadog issue 3023973c-a83f-11ef-8278-da7ad0900002).
const SUPPRESSED_MUTATION_ERROR_MESSAGES = new Set([
  "Attempt to access private route without an auth token is forbidden.",
]);

/**
 * smartFetch throws the raw `Response` on non-OK HTTP. Logging that object
 * via console.error prints the useless string "Response" in Playwright /
 * Chromium (and RUM), while the UI already surfaces the API message. Skip
 * Response instances here; real Error failures still log.
 *
 * A 401 Error (`handleApiError` copies the HTTP status onto it) is a refused
 * login — a wrong password, not a fault. Logging it paged #eng-errors on every
 * typo and put the user's email into RUM (issue #6872); the login UI already
 * shows the refusal. 403, 404, 5xx and status-less Errors still log.
 */
function shouldSuppressMutationConsoleError(e: unknown): boolean {
  if (typeof Response !== "undefined" && e instanceof Response) {
    return true;
  }
  if (!(e instanceof Error)) {
    return false;
  }
  if ((e as Error & { status?: unknown }).status === 401) {
    return true;
  }
  return SUPPRESSED_MUTATION_ERROR_MESSAGES.has(e.message);
}

/**
 * The parked MFA step is control flow, not a failure: `getProject` and
 * `getFoldersAndTickets` throw `EnterpriseMfaStepParkedError` to say "this
 * resource is behind a TOTP step, the dialog is open". React Query cannot know
 * that, so with the default three attempts each parked read re-issues the
 * resource GET and re-dispatches `openEnterpriseMfaSwitch` — on the polling
 * callers (`useTicketView`, `useChatReviewSummaries`, 5s) that re-opens the
 * dialog the user is looking at, on every tick, before the error component ever
 * renders. Retrying cannot succeed either: the same session re-reads the same
 * step until the code clears, so the attempt is pure cost.
 *
 * Everything else keeps the library default (`< 3`), deliberately — this
 * predicate exists to carve out one sentinel, not to re-tune retries.
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isEnterpriseMfaStepParkedError(error)) {
    return false;
  }
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: shouldRetryQuery,
    },
    mutations: {
      onError(e, _variables, _onMutateResult, context) {
        if (shouldSuppressMutationConsoleError(e)) {
          return;
        }
        // why: a dropped connection is the user's network, not a fault. Logged at error it became a RUM event that paged #eng-errors on every blip (#7977, #7543); warn keeps the mutation key for debugging without paging.
        if (isFetchNetworkError(e)) {
          console.warn("Mutation failed on a dropped connection", {
            mutationKey: context?.mutationKey,
          });
          return;
        }
        console.error(e);
      },
    },
  },
});

export const QueryClientContext: FC<PropsWithChildren> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
